import React, { useState } from 'react';
import classnames from 'classnames/bind';
import styles from './CompactAccordion.module.scss';

const cx: CX = classnames.bind(styles);

interface CompactAccordionProps {
    title: string;
    children: React.ReactNode;
    level?: 1 | 2 | 3;
    defaultExpanded?: boolean;
    onTitleClick?: () => void;
    actions?: React.ReactNode;
    className?: string;
}

function CompactAccordion({
    title,
    children,
    level = 1,
    defaultExpanded = false,
    onTitleClick,
    actions,
    className,
}: CompactAccordionProps) {
    // Level 3 items are always expanded (no collapse state)
    const [isExpanded, setIsExpanded] = useState(level === 3 ? true : defaultExpanded);

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        // Level 3 (sub-sub-sections) should not be expandable
        if (level === 3) return;
        setIsExpanded(!isExpanded);
    };

    const handleTitleClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent bubbling to header click
        if (onTitleClick) {
            onTitleClick();
        }
    };

    const handleHeaderClick = () => {
        // Level 3 (sub-sub-sections) should not be expandable
        if (level === 3) return;
        setIsExpanded(!isExpanded);
    };

    return (
        <div className={cx('compact-accordion', `level-${level}`, className, { expanded: isExpanded })}>
            <div className={cx('header')} role="button" tabIndex={0} onClick={handleHeaderClick}>
                {level !== 3 && (
                    <button
                        type="button"
                        className={cx('toggle-button')}
                        onClick={handleToggle}
                        aria-expanded={isExpanded}
                    >
                        <svg
                            className={cx('chevron-icon')}
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M9 18L15 12L9 6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                )}
        
                <div className={cx('title-section')}>
                    <span className={cx('title')} onClick={handleTitleClick} role="button" tabIndex={0}>{title}</span>
                </div>
        
                {actions && (
                    <div className={cx('actions')} onClick={(e) => e.stopPropagation()} role="button" tabIndex={0}>
                        {actions}
                    </div>
                )}
            </div>
      
            {isExpanded && level !== 3 && (
                <div className={cx('content')}>
                    {children}
                </div>
            )}
        </div>
    );
}

CompactAccordion.defaultProps = {
    level: 1,
    defaultExpanded: false,
    onTitleClick: undefined,
    actions: undefined,
    className: undefined,
};

export default CompactAccordion; 